/// <reference types="vitest/importMeta" />

export type Mutable<T> = { -readonly [P in keyof T]: T[P] }

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]]
type Join<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}${'' extends P ? '' : '.'}${P}`
    : never
  : never
// keyof T with nested subobjects keys. See https://stackoverflow.com/a/58436959/14655658
export type Paths<T, D extends number = 5> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]: K extends string
          ? NonNullable<T[K]> extends Array<infer U>
            ?
                | `${K}`
                | `${K}[${number}]`
                | `${K}[${number}].${Paths<U, Prev[D]>}`
                | `${K}.${number}`
                | `${K}.${number}.${Paths<U, Prev[D]>}`
            : NonNullable<T[K]> extends object
              ? `${K}` | `${K}.${Paths<NonNullable<T[K]>, Prev[D]>}`
              : `${K}`
          : never
      }[keyof T]
    : never

export type Leaves<T, D extends number = 5> = [D] extends [never]
  ? never
  : T extends object
    ? { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T]
    : ''

export function reverseMap<T1 extends string | number | symbol, T2 extends string | number | symbol> (
  obj: Record<T1, T2>
): Partial<Record<T2, T1>> {
  const inverted: Partial<Record<T2, T1>> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (inverted[value] == null) {
        inverted[value] = key
      }
    }
  }

  return inverted
}

export function isKey<T extends object> (obj: T, key: keyof any): key is keyof T {
  return key in obj
}

export function toEnum<T extends string> (
  input: string,
  enumObj: { [key: string]: T },
  toUpperCase = false
): T | undefined {
  const isNotEnumerable = !Object.values(enumObj)
    .map(it => it.toLowerCase())
    .includes(input.toLowerCase() as T)

  if (isNotEnumerable) {
    return
  }

  return toUpperCase ? (input.toUpperCase() as T) : (input.toLowerCase() as T)
}

export function groupBy<K extends keyof any, T> (items: T[], keySelector: (item: T) => K): Partial<Record<K, T[]>> {
  return items.reduce((acc: Partial<Record<K, T[]>>, item) => {
    const group = keySelector(item)
    if (!acc[group]) {
      acc[group] = []
    }
    acc[group]!.push(item)
    return acc
  }, {})
}

if ((import.meta as any).vitest) {
  const { it, describe, expect } = (import.meta as any).vitest

  enum TestStringEnum {
    Test = 'test'
  }

  describe.concurrent('toEnum', () => {
    it('should return an element, when enum has value', () => {
      expect(toEnum('test', TestStringEnum)).toBe(TestStringEnum.Test)
    })

    it('should return an element in upper case, when has a upper case option', () => {
      expect(toEnum('test', TestStringEnum, true)).toBe(TestStringEnum.Test.toUpperCase())
    })

    it('should return null, when enum does not have a matching value', () => {
      expect(toEnum('aaaa', TestStringEnum)).toBeUndefined()
    })
  })
}
